import type { TranslationResources } from '../../types';

export const en = {
  common: {
    labOpening: 'Opening your Workspace in Mylo lab…',
    labLaunchError:
      'Mylo lab could not open. Check your connection and try again, or contact support if Lab is unavailable in this region.',
    labRetry: 'Try again',

    brand: 'Mylo Studio',
    continue: 'Continue',
    cancel: 'Cancel',
    save: 'Save changes',
    reset: 'Reset demo data',
    skip: 'Skip to content',
    search: 'Search',
    new: 'New',
    beta: 'Beta',
    open: 'Open',
    back: 'Back',
    language: 'Language',
    english: 'English',
    russian: 'Russian',
    chinese: 'Simplified Chinese',
    credits: 'Soaps',
    sandbox: 'Sandbox · international region',
    noResults: 'Nothing here yet',
    viewAll: 'View all',
    comingSoon: 'Coming soon',
    errorStart: 'Mylo Studio could not start.',
    reload: 'Reload workspace',
    switchToLightTheme: 'Switch to light theme',
    switchToDarkTheme: 'Switch to dark theme',
    download: 'Download',
    signOut: 'Sign out',
    enterStudio: 'Enter Mylo Studio',
    demoHint: 'Use email code sign-in while OAuth setup is pending.',
    metricCreative: 'Creative variants',
    metricMarket: 'Market context',
    metricAgent: 'Agent activity',
    paidBeta: 'Paid beta · production environment',
    signIn: 'Log in',
    startCreating: 'Start creating',
  },
  home: {
    skip: 'Skip to content',
    homeLabel: 'Mylo Studio home',
    sectionsLabel: 'Homepage sections',
    galleryLabel: 'Videos created with the Agent',
    nav: ['Capabilities', 'Examples', 'How It Works', 'Models'],
    actions: ['Open Agent', 'Create a Video', 'View Examples'],
    hero: {
      badge: 'AI Video Agent',
      title: 'Turn any idea into a video.',
      titleLines: ['Turn', 'any idea', 'into a video.'],
      description:
        'Write in Russian. Add a photo, a video, or just an idea. Mylo Agent understands the task, chooses a model, and helps turn it into a finished video.',
      highlights: [
        'Speak Russian',
        'No prompt language. Just explain what you want.',
        'One Agent. The best models.',
        'Technology changes. You do not have to keep up.',
      ],
      prompt: [
        'Prompt',
        'Make this feel like a neon music video from the future.',
        'Agent Understood',
        'Agent Is Creating',
        'Live Preview',
        'VIDEO',
      ],
    },
    overview: [
      'Your AI video studio',
      'One idea. One Agent. Endless videos.',
      'A meme, edit, story, music video, character, or an entirely new world — share your idea with the Agent. It helps bring it to life through a script, scenes, and video.',
    ],
    formats: ['Idea', 'Reference', 'Agent', 'Video'],
    showcase: [
      'What you can create',
      'Scroll. Watch. Create your own.',
      'One idea can become dozens of different videos. Change the style, characters, atmosphere, and direction — the Agent helps make a new version.',
      'Idea / Agent / Video',
    ],
    materials: ['How it works', 'Start with anything.', 'Idea / Photo / Video'],
    pipeline: [
      ['Idea', 'text + photo + video'],
      ['Understanding', 'style + character + scenes'],
      ['Agent', 'model + generation'],
      ['Video', 'finished result'],
    ],
    submitItems: [
      'Write an idea or upload photos, videos, and references.',
      'The Agent understands the style, character, mood, and result you want.',
      'The Agent chooses the right model and prepares the generation.',
      'Get your video, change the details, and create the next version.',
    ],
    context: [
      'In Russian. No prompt language.',
      'Talk to the Agent like a person.',
      'You do not need to learn perfect prompts. Explain your idea in Russian — the Agent turns it into a task AI models understand.',
      'Homepage videos are silent examples. Open the Agent to create your own version.',
    ],
    groups: [
      ['You provide', 'An idea', 'A photo or video', 'References', 'A mood'],
      ['The Agent creates', 'A script', 'Scenes', 'Prompts', 'Finished videos'],
    ],
    timeline: [
      'New models. More possibilities.',
      'One Agent — access new models in one place.',
      'We add new models',
      'The Agent knows which one to use',
      'You just create',
    ],
    footer: [
      'Your next idea',
      'What will you create first?',
      'Open the Agent. Describe your idea in Russian — and see what it becomes.',
    ],
    footerLegalNote: 'Legal information for the RU region.',
    legalNavLabel: 'Legal',
    legalOffer: 'Terms of Service',
    legalPrivacy: 'Privacy Policy',
    legalPersonalDataConsent: 'Cookie Policy',
    clips: [
      ['Neon music video', 'Music video'],
      ['Luxury launch', 'Style'],
      ['Race to the limit', 'Action'],
      ["A hero's story", 'Story'],
      ['Dramatic entrance', 'Character'],
      ['Cut to the beat', 'Edit'],
      ['Night city', 'Atmosphere'],
      ['The red thread', 'Story'],
      ['Agent creates video', 'Finished videos'],
      ['Visual reference', 'Reference'],
      ['Motion without limits', 'Motion'],
      ['A new world', 'Atmosphere'],
      ['How video is made', 'Process'],
    ],
    stories: [
      ['Neon music video', 'An idea becomes a music video from the future.'],
      [
        'Luxury launch',
        'A reference sets the light, rhythm, and premiere mood.',
      ],
      [
        'Race to the limit',
        'The Agent shapes speed and tension into one coherent scene.',
      ],
      [
        "A hero's story",
        'A character and an idea become a short visual story.',
      ],
      ['Dramatic entrance', 'One moment becomes a scene with character.'],
      [
        'Cut to the beat',
        'Motion, music, and transitions become an energetic edit.',
      ],
      [
        'Night city',
        'City lights set the atmosphere and direction for a new version.',
      ],
      ['The red thread', 'One visual motif ties the whole story together.'],
    ],
  },
  auth: {
    eyebrow: 'AI platform for video creation',
    title: 'Create promotional materials for the Russian-speaking market.',
    subtitle:
      'Turn product truth, references and ideas into a system of testable video concepts.',
    google: 'Continue with Google',
    vk: 'Continue with VK',
    emailAddress: 'Email address',
    verificationCode: 'Verification code',
    sendCode: 'Send code',
    loginWithEmail: 'Log in with email',
    emailWorking: 'Checking…',
    codePrinted: 'Code printed in the server terminal.',
    emailFailed: 'Email sign-in failed. Check the code and try again.',
    terms:
      'By continuing, you agree to the <termsLink>Terms of Service</termsLink> and <privacyLink>Privacy Policy</privacyLink>.',
    working: 'Opening {{provider}} demo sign-in…',
    success: 'Signed in.',
    cancelled: 'Sign-in was cancelled. Try another provider.',
    failed: 'Sign-in failed. Try again.',
    email: 'Email',
    password: 'Password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    displayName: 'Display name',
    register: 'Create account',
    login: 'Log in',
    registerTitle: 'Create your account',
    registerSubtitle: 'Enter your details to get started.',
    loginTitle: 'Welcome back',
    loginSubtitle: 'Sign in to your account.',
    orContinueWith: 'Or continue with',
    alreadyHaveAccount: 'Already have an account?',
    dontHaveAccount: "Don't have an account?",
    passwordMinLength:
      'At least 10 characters and Strong strength required. Longer passphrases help.',
    confirmPassword: 'Confirm password',
    passwordsDoNotMatch: 'Passwords do not match',
    passwordTooShort: 'Password must be at least 10 characters.',
    passwordTooWeak:
      'Password must reach Strong strength. Use a longer, less predictable passphrase.',
    passwordValidationUnavailable:
      'Password validation is temporarily unavailable. Please retry.',
    retry: 'Retry',
    passwordStrength: {
      score0: 'Very weak',
      score1: 'Weak',
      score2: 'Fair',
      score3: 'Good',
      score4: 'Strong',
    },
    emailRequired: 'Email is required',
    passwordRequired: 'Password is required',
    invalidEmail: 'Please enter a valid email',
    emailAlreadyExists: 'An account with this email already exists',
    invalidCredentials: 'Invalid email or password',
    region: {
      boundary: 'Data boundary',
      title: 'Choose where your data lives',
      description:
        'Select a Region before signing in. Accounts and Workspaces are separate, and data cannot be moved directly between Regions.',
      russia: 'Russia',
      russiaDetail:
        'Your account and Workspace data stay in the Russia Region.',
      global: 'International',
      globalDetail:
        'Your account and Workspace data stay in the international Region.',
      continue: 'Continue in {{region}} →',
      connecting: 'Connecting to {{region}}…',
      unavailable: '{{region}} unavailable',
      blocked: 'Region connection blocked',
      verifyFailed: 'The selected API could not be verified.',
      noLogin: 'Could not send the sign-in request.',
      retry: 'Retry',
      choose: 'Choose Region',
    },
  },
  navigation: {
    account: 'Account',
    menu: 'Open navigation',
    collapseSidebar: 'Collapse sidebar',
    expandSidebar: 'Expand sidebar',
  },
  onboarding: {
    step: 'Company setup',
    title: 'Create your first workspace',
    subtitle:
      'Enter your company, primary market, and data storage region to set up the workspace.',
    name: 'Company name',
    website: 'Website',
    industry: 'Industry',
    size: 'Company size',
    jobTitle: 'Your role',
    market: 'Primary market',
    region: 'Data region',
    regionHint:
      'Data region is locked after creation. Create another company to use a different region.',
    logo: 'Company logo',
    chooseLogo: 'Choose file',
    noLogoSelected: 'No file selected',
    create: 'Create workspace',
    industries: {
      ecommerce: 'E-commerce',
      agency: 'Marketing agency',
      gaming: 'Games & apps',
      brand: 'Consumer brand',
      other: 'Other',
    },
    sizes: {
      small: '1–10',
      medium: '11–50',
      large: '51–250',
      enterprise: '250+',
    },
    markets: { ru: 'Russia', cis: 'CIS', global: 'Global' },
  },
  billing: {
    title: 'Plans & Billing',
    sharedBy: 'Soaps are shared by everyone in {{workspace}}.',
    sharedBalance: 'Shared Balance',
    credits: 'credits',
    choosePlan: 'Choose a Plan',
    displayOnly: 'Display prices only. Checkout is not connected yet.',
    secureCheckout: 'Payments are completed securely with YooKassa.',
    continueToPayment: 'Continue to payment',
    pendingPlanPayment:
      'You have an unfinished {{plan}} {{cadence}} order ({{amount}})',
    annualCadence: 'annual',
    monthlyCadence: 'monthly',
    continuePendingPayment: 'Continue previous payment',
    abandonPendingPayment: 'Abandon and choose again',
    abandoningPendingPayment: 'Abandoning payment…',
    abandonPendingPaymentError:
      'This payment cannot be abandoned yet. Continue it or try again later.',
    openingCheckout: 'Opening secure payment…',
    annualUnavailable: 'Annual payment coming soon',
    annualAfterCurrentTerm:
      'Annual billing is available after your current paid term ends.',
    annualTermLocked:
      'Plan changes are available after your current annual term ends.',
    checkoutError: 'Payment could not be started. Try again.',
    insufficientCreditsTitle: 'Not enough Soaps for this generation',
    insufficientCreditsNeed: 'This generation needs',
    insufficientCreditsHave: '. You have',
    insufficientCreditsNeedMore: ', so you need',
    insufficientCreditsMoreSuffix: ' more.',
    requiredCreditsOnlyPrefix: 'This generation needs',
    requiredCreditsOnlySuffix: '.',
    insufficientCreditsSummary:
      'This generation needs {{required}} credits. You have {{available}}, so you need {{shortage}} more.',
    requiredCreditsOnly: 'This generation needs {{required}} credits.',
    loadingPlans: 'Loading plans…',
    plansLoadError: '{{message}} Close this dialog and try again.',
    buyCredits: 'Buy Soaps',
    getCredits: 'Get Soaps',
    workspaceCredits: 'Workspace Soaps',
    renewsOn: 'Soaps refresh on {{date}}',
    creditRefreshOn: 'Soaps refresh on {{date}}',
    planEndsOn: 'Plan access through {{date}}',
    noAutomaticRenewal: 'No automatic renewal',
    noScheduledRefresh: 'No scheduled Soap refresh',
    renewYourPlan: 'Renew your plan',
    planExpiredTitle: 'Your plan has expired',
    planExpiredDescription:
      'Renew your subscription to continue generating. Purchased Soaps remain in your workspace; adding Soaps does not renew your plan.',
    watermarkUpgradeDescription:
      'Upgrade to Pro to download original images and videos without watermarks.',
    upgradeToPro: 'Upgrade to Pro',
    upgradeYourPlan: 'Upgrade your plan',
    comingSoon: 'Coming soon',
    creditsComingSoon:
      'Soap purchases are coming soon. For now, an operator can add purchased Soaps to this workspace.',
    creditPackDescription: 'Add Soaps without changing your current plan.',
    creditPackRequiresPlan: 'Credit packs require an active paid plan.',
    oneTimeCreditPack: 'One-time credit pack',
    creditPackNoRenewal: 'One-time payment. No automatic renewal.',
    creditPackPaidPlanOnly: 'Available while your paid plan is active.',
    buyCreditPack: 'Buy 1,000 Soaps',
    current: 'Current',
    currentPlan: 'Current Plan',
    perMonth: 'per month',
    once: 'once',
    monthly: 'Monthly',
    yearlyDiscount: 'Yearly, {{discount}}% off',
    billingCadence: 'Billing cadence',
    billedAnnually: 'Billed annually',
    annualTotal: '{{price}} per year',
    customPricing: 'Custom pricing',
    customCredits: 'Custom credits',
    planNames: {
      FREE: 'Free',
      STANDARD: 'Standard',
      PRO: 'Pro',
      MAX: 'Max',
      ENTERPRISE: 'Enterprise',
    },
    badges: { POPULAR: 'Popular', BEST_VALUE: 'Best value' },
    entitlements: {
      CORE_AGENT: 'AI creative agent',
      IMAGE_AND_VIDEO_TOOLS: 'Image and video generation tools',
      PRIVATE_ASSET_LIBRARY: 'Private Workspace asset library',
      ECONOMY_VIDEO_MODEL: 'Мыло Video 2.0 Mini economy model, up to 720p',
      INCLUDES_FREE: 'Everything in Free',
      ALL_PRODUCTION_MODELS: 'All production models',
      MONTHLY_CREDIT_REFRESH: 'Soaps refresh monthly',
      CREDIT_PACK_PURCHASES: 'Can purchase additional Soap packs',
      VIDEO_UP_TO_1080P: 'All Мыло Video models, up to 1080p',
      INCLUDES_STANDARD: 'Everything in Standard',
      HIGHER_MONTHLY_GENERATION_ALLOWANCE: 'More generations every month',
      API_ACCESS: 'Developer API access',
      WATERMARK_FREE_VIDEOS: 'Watermark-free video downloads',
      WATERMARK_FREE_IMAGES: 'Watermark-free image downloads',
      WATERMARK_FREE_VIDEO_EDITOR_EXPORTS:
        'Watermark-free video editor exports',
      VIDEO_UP_TO_4K: '4K video generation',
      INCLUDES_PRO: 'Everything in Pro',
      HIGHEST_MONTHLY_GENERATION_ALLOWANCE:
        'Highest monthly generation allowance',
      ONE_MONTH_CREDIT_ROLLOVER: 'Unused Soaps roll over one month',
      INCLUDES_MAX: 'Everything in Max',
      CUSTOM_CREDITS: 'Custom Soap amount',
      CUSTOM_TEAM_SIZE: 'Custom member count',
      ENTERPRISE_SUPPORT: 'Enterprise onboarding and support',
    },
    includedCreditsLabel: 'Included',
    includedCredits: 'Included credits: {{count}}',
    upgradePlan: 'Upgrade Plan',
    creditTimeline: 'Credit Timeline',
    timelineHint: 'See which shared credits expire first.',
    noGrants: 'No credit grants yet.',
    expires: 'Expires {{date}}',
    neverExpires: 'Never expires',
    planCredits: 'Plan Soaps',
    carriedCredits: 'Carried Soaps',
    purchasedCredits: 'Purchased Soaps',
    promotionalCredits: 'Promotional Soaps',
    existingCredits: 'Existing Soaps',
    checkoutSoon:
      'Checkout is coming soon. An operator can currently update your workspace plan and Soaps.',
    chooseWorkspace: 'Choose a workspace to view billing.',
    loading: 'Loading billing…',
    loadError: 'Billing could not load',
    loadErrorHint: 'Refresh the page to try again.',
    creditUsage: 'Soap usage',
    creditUsageHint: 'Review every change to this workspace’s Soaps.',
    activityTypeFilter: 'Activity type',
    allActivityTypes: 'All activity',
    activityLoading: 'Loading credit activity…',
    activityLoadError: 'Credit activity could not load. Try again.',
    noActivity: 'No credit activity yet.',
    previous: 'Previous',
    next: 'Next',
    activityColumns: {
      date: 'Date',
      event: 'Event',
      type: 'Type',
      change: 'Credit change',
      status: 'Status',
    },
    activityTypes: {
      GENERATION: 'Generation',
      TOP_UP: 'Top-up',
      PLAN_GRANT: 'Plan grant',
      CARRYOVER: 'Carryover',
      PROMOTION: 'Promotion',
      REFUND: 'Refund',
      ADMIN_ADJUSTMENT: 'Adjustment',
      EXPIRY: 'Expiry',
      OTHER: 'Other',
    },
    activityStatuses: {
      COMPLETED: 'Completed',
      HELD: 'Held',
      RELEASED: 'Released',
    },
    credit: {
      topUp: 'Credits added',
      generationHeld: 'Generation credits held',
      labUsed: 'Credits charged for Mylo lab',
      generationUsed: 'Credits charged for generation',
      generationReleased: 'Reserved credits returned',
      refund: 'Credits refunded',
      adminAdjustment: 'Credit adjustment',
      planGrant: 'Plan credits issued',
      carryover: 'Credits carried over',
      promotion: 'Promotional credits issued',
      existingBalance: 'Existing balance imported',
      expired: 'Credits expired',
      merchantAdjustment: 'Merchant credit adjustment',
      other: 'Credit activity',
    },
  },
  developerApi: {
    eyebrow: 'Workspace developer access',
    title: 'MyloStudio API',
    description:
      'Create server-side credentials for {{workspace}} and pay for image and video generation with workspace credits.',
    activeKeys: '{{count}} active keys',
    proBenefit: 'Pro benefit',
    lockedTitle: 'Build with the MyloStudio API',
    lockedDescription:
      'Upgrade to Pro or higher to create API keys and use workspace credits through the Developer API.',
    upgradeToPro: 'Upgrade to Pro',
    keyName: 'Key name',
    keyNamePlaceholder: 'Production server…',
    expiresAt: 'Expires (optional)',
    permissions: 'Permissions',
    createKey: 'Create API key',
    creating: 'Creating…',
    keysTitle: 'API keys',
    maxKeys: 'Up to 5 active keys',
    loading: 'Loading API keys…',
    emptyTitle: 'No API keys yet',
    emptyDescription:
      'Name the system that will use this key, then create it above.',
    active: 'Active',
    expired: 'Expired',
    revoked: 'Revoked',
    keyMeta: 'Created by {{creator}} on {{date}} · Last used {{lastUsed}}',
    never: 'never',
    revoke: 'Revoke',
    revokeConfirm:
      'Revoke “{{name}}”? Applications using it will stop immediately.',
    quickStart: 'Quick start',
    quickStartDescription:
      'Keep keys on your server. Never expose them in browser code or URLs.',
    viewApiDocs: 'View API docs',
    imageExample: 'Image generation',
    videoExample: 'Video generation',
    createdTitle: 'Copy your API key',
    createdDescription:
      'This secret is shown once. Store it in your server secret manager before closing.',
    copyKey: 'Copy key',
    copied: 'Copied',
    done: 'Done',
    chooseWorkspace: 'Choose a workspace to manage its API keys.',
    loadError: 'Could not load API keys.',
    createError: 'Could not create the API key.',
    revokeError: 'Could not revoke the API key.',
  },
  account: {
    title: 'Account settings',
    workspaceSettings: 'Workspace Settings',
    workspaceSettingsTab: 'Settings',
    workspaceMembersTab: 'Members',
    workspaceBillingTab: 'Plans & Billing',
    workspaceEyebrow: 'Workspace administration',
    workspaceSettingsDescription:
      'Review your workspaces and maintain the details your team shares.',
    workspaceDetails: 'Workspace details',
    workspaceDetailsHint:
      'Owners and admins can update the shared workspace profile.',
    workspaceName: 'Workspace name',
    workspaceNameRequired: 'Enter a workspace name.',
    workspaceUpdated: 'Workspace updated',
    workspaceUpdateFailed: 'Could not update this workspace. Try again.',
    workspaceReadOnly: 'Only owners and admins can edit this workspace.',
    currentWorkspace: 'Current workspace',
    noWorkspaces: 'No workspaces are available.',
    createNewWorkspace: 'Create new workspace',
    createWorkspace: 'Create Workspace',
    manageWorkspaces: 'Manage workspaces',
    regionLabel: 'Region {{region}}',
    activeRegionName: 'ALPHA',
    close: 'Close',
    createWorkspaceFailed: 'Could not create the workspace. Try again.',
    switchWorkspaceFailed: 'Could not switch workspaces. Try again.',
    plansAndBilling: 'Plans and Billing',
    accountProfile: 'Account Profile',
    profileDescription:
      'Manage how your account appears and review your sign-in details.',
    changeAvatar: 'Change avatar',
    avatarHint: 'JPG, PNG, or WebP. Maximum 5 MB.',
    uploadingAvatar: 'Uploading {{progress}}%',
    avatarTypeError: 'Choose a JPG, PNG, or WebP image.',
    avatarSizeError: 'Choose an image smaller than 5 MB.',
    avatarUploadFailed: 'Avatar upload failed. Try again.',
    avatarUpdated: 'Avatar updated',
    userId: 'User ID',
    copyUserId: 'Copy user ID',
    copied: 'Copied',
    signInMethods: 'Sign-in methods',
    signInMethodsHint: 'Sign-in methods linked to your Mylo account.',
    connected: 'Connected',
    notConnected: 'Not connected',
    saveChanges: 'Save changes',
    saving: 'Saving…',
    changesSaved: 'Changes saved',
    saveFailed: 'Could not save your changes. Try again.',
    displayNameRequired: 'Enter a display name.',
    upgradePlan: 'Upgrade your plan',
    developerApi: 'Developer API',
    inviteMembers: 'Invite members',
    referFriend: 'Refer a friend',
    workspace: 'Workspace',
    theme: 'Theme',
    themeOptions: 'Theme options',
    languageOptions: 'Language options',
    system: 'System',
    light: 'Light',
    dark: 'Dark',
    switchWorkspace: 'Switch Workspace',
    logOut: 'Log out',
    profile: 'Profile',
    security: 'Security',
    companies: 'Companies',
    members: 'Members',
    language: 'Language',
    preferences: 'Preferences',
    wallet: 'Wallet',
    displayName: 'Display name',
    email: 'Email',
    provider: 'Sign-in method',
    providers: 'Login providers',
    current: 'Current',
    switchCompany: 'Switch company',
    addCompany: 'Add company',
    dataRegion: 'Data region',
    appearance: 'Appearance',
    notifications: 'Generation notifications',
    resetHint: 'Clear the local demo session, companies, projects and assets.',
    confirmReset: 'Reset the demo session, companies, projects and assets?',
    balance: 'Balance',
    credits: 'Credits',
    currency: 'Currency',
    creditRecords: 'Credit records',
    noCreditRecords: 'No credit records yet.',
    filterType: 'Type',
    filterDateFrom: 'From',
    filterDateTo: 'To',
    allTypes: 'All types',
    resetFilter: 'Reset',
    applyFilter: 'Apply',
    ledgerType: {
      TOP_UP: 'Top up',
      HOLD: 'Reservation',
      CAPTURE: 'Capture',
      RELEASE: 'Reservation released',
      REFUND: 'Refund',
      ADMIN_ADJUSTMENT: 'Admin adjustment',
      SUBSCRIPTION_CHARGE: 'Subscription charge',
      PROMOTIONAL_CREDIT: 'Bonus credits added',
      EXPIRED_CREDIT: 'Expired credit',
      MERCHANT_CREDIT_ADJUSTMENT: 'Merchant credit adjustment',
    },
    topUp: 'Top up',
    transactionType: 'Type',
    transactionAmount: 'Amount',
    transactionDate: 'Date',
    linkedAccounts: 'Linked accounts',
    unlink: 'Unlink',
    link: 'Link',
    activeDevices: 'Active devices',
    thisDevice: 'This device',
    unknownDevice: 'Unknown device',
    expires: 'Expires',
    revoke: 'End session',
    dangerZone: 'Danger zone',
    deleteWorkspace: 'Delete workspace',
    deleteWorkspaceHint: 'The workspace can be restored for 30 days.',
    role: 'Role',
    owner: 'Owner',
    admin: 'Admin',
    member: 'Member',
    personalInfo: 'Personal info',
    connectedAccounts: 'Connected accounts',
    workspaceList: 'Workspaces',
    selectWorkspace: 'Select a workspace.',
    yourRole: 'Your role is {{role}}.',
    regionLocked: 'Data storage region: {{region}}. It cannot be changed.',
    you: 'you',
    vkIdentity: 'VK identity',
    transferOwnership: 'Transfer ownership',
    remove: 'Remove',
    invitePeople: 'Invite people',
    inviteExpiryHint: 'Links expire after seven days and can be accepted once.',
    invitationRole: 'Role for new invitation',
    createSecureLink: 'Create secure link',
    invitationLink: 'Invitation link',
    copy: 'Copy',
    pending: 'Pending',
    accepted: 'Accepted',
    revoked: 'Revoked',
    expired: 'Expired',
    transferOwnershipTitle: 'Transfer ownership?',
    removeMemberTitle: 'Remove member?',
    revokeInvitationTitle: 'Revoke invitation?',
    transferOwnershipDescription: 'Transfer this workspace to {{name}}?',
    removeMemberDescription: 'Remove {{name}} from this workspace?',
    revokeInvitationDescription:
      'This invitation link will stop working immediately.',
    cancel: 'Cancel',
    confirm: 'Confirm',
    membersLoadError: 'Workspace members could not be loaded.',
    deviceExpires: 'Session ends {{date}}',
    deletedWorkspaces: 'Deleted workspaces',
    deletedWorkspacesHint:
      'Owners can restore a workspace during its 30-day recovery period.',
    purgeDate: 'Purge {{date}}',
    purgePending: 'Purge pending',
    restore: 'Restore',
    noDeletedWorkspaces: 'No deleted workspaces.',
    joiningWorkspace: 'Joining workspace…',
    inviteAcceptError: 'Invite could not be accepted.',
  },
  validation: {
    required: 'This field is required.',
    website: 'Enter a valid website URL.',
    logoSize: 'Choose an image smaller than 5 MB.',
  },
  studio: {
    campaignBriefForm: {
      targetAudience: 'Target audience',
      platforms: 'Platforms',
      campaignGoal: 'Campaign goal',
      keyMessage: 'Key message',
      tone: 'Tone',
      cta: 'Call to action',
      constraints: 'Constraints',
      targetAudiencePlaceholder: 'e.g. urban professionals, 25-34',
      platformsPlaceholder: 'e.g. Instagram Reels, TikTok',
      campaignGoalPlaceholder: 'e.g. product launch awareness',
      keyMessagePlaceholder: 'e.g. visibly brighter skin in 7 days',
      tonePlaceholder: 'e.g. clean, premium, confident',
      ctaPlaceholder: 'e.g. Shop the set',
      constraintsPlaceholder: 'e.g. avoid medical claims, keep packaging white',
      custom: 'Custom',
      customLabel: 'Custom {{field}}',
      targetAudienceQuestion: 'Who should these campaign images speak to?',
      platformsQuestion: 'Where should these still assets run?',
      campaignGoalQuestion: 'What should this image campaign achieve?',
      closeTool: 'Close form',
      previousQuestion: 'Previous question',
      nextQuestion: 'Next question',
      required: 'required',
      requiredHint: 'Answer the required groups to continue.',
      skip: 'Skip',
      stepOf: 'of',
      typeOwnAnswer: 'Type your own answer',
      targetAudienceOptionGenZ: 'Gen Z trend seekers',
      targetAudienceOptionGenZHint:
        'Social-first buyers drawn to visual trends',
      targetAudienceOptionProfessionals: 'Urban professionals',
      targetAudienceOptionProfessionalsHint:
        'Premium everyday-use scenes for busy routines',
      targetAudienceOptionParents: 'New parents',
      targetAudienceOptionParentsHint:
        'Practical proof points and calm product clarity',
      targetAudienceOptionFitness: 'Fitness enthusiasts',
      targetAudienceOptionFitnessHint:
        'Performance comparison with a premium lens',
      platformsOptionInstagram: 'Instagram feed + Stories',
      platformsOptionInstagramHint:
        'Assets tailored for the Instagram feed and Stories',
      platformsOptionTikTok: 'TikTok image posts',
      platformsOptionTikTokHint: 'Product images that are clear at a glance',
      platformsOptionXhs: 'Xiaohongshu notes',
      platformsOptionXhsHint:
        'Assets that introduce the product and clearly show its benefits and details',
      platformsOptionMultiStatic: 'Multi-platform stills',
      platformsOptionMultiStaticHint:
        'Versatile assets for organic and paid posts',
      campaignGoalOptionLaunch: 'Product launch',
      campaignGoalOptionLaunchHint:
        'Introduce the product and create first-wave demand',
      campaignGoalOptionConversions: 'Drive conversions',
      campaignGoalOptionConversionsHint:
        'Push viewers toward purchase or sign-up',
      campaignGoalOptionBenefits: 'Explain benefits',
      campaignGoalOptionBenefitsHint:
        'Make the core value obvious and memorable',
      campaignGoalOptionRetarget: 'Retarget shoppers',
      campaignGoalOptionRetargetHint:
        'Bring interested audiences back and prompt them to act',
      submit: 'Create visual concepts',
    },
    videoBriefForm: {
      targetAudience: 'Target audience',
      platforms: 'Platforms',
      campaignGoal: 'Campaign goal',
      hook: 'Hook (first 3 seconds)',
      keyMessage: 'Key message',
      tone: 'Tone',
      cta: 'Call to action',
      constraints: 'Constraints',
      targetAudiencePlaceholder: 'e.g. urban professionals, 25-34',
      platformsPlaceholder: 'e.g. TikTok, Instagram Reels',
      campaignGoalPlaceholder: 'e.g. product launch awareness',
      hookPlaceholder: 'e.g. product slam-cut with bold claim',
      keyMessagePlaceholder:
        'e.g. lighter carry, stronger battery, cleaner finish',
      tonePlaceholder: 'e.g. kinetic, premium, candid',
      ctaPlaceholder: 'e.g. Try it today',
      constraintsPlaceholder: 'e.g. keep the logo visible, avoid price claims',
      custom: 'Custom',
      customLabel: 'Custom {{field}}',
      targetAudienceQuestion: 'Who is this ad speaking to?',
      platformsQuestion: 'Where should this video run?',
      campaignGoalQuestion: 'What should this video achieve?',
      closeTool: 'Close form',
      previousQuestion: 'Previous question',
      nextQuestion: 'Next question',
      required: 'required',
      requiredHint: 'Answer the required groups to continue.',
      skip: 'Skip',
      stepOf: 'of',
      typeOwnAnswer: 'Type your own answer',
      targetAudienceOptionGenZ: 'Gen Z trend seekers',
      targetAudienceOptionGenZHint:
        'Short-form buyers watching creator-led trends',
      targetAudienceOptionProfessionals: 'Urban professionals',
      targetAudienceOptionProfessionalsHint:
        'Premium daily-use messaging for busy routines',
      targetAudienceOptionParents: 'New parents',
      targetAudienceOptionParentsHint:
        'Practical proof points and time-saving benefits',
      targetAudienceOptionFitness: 'Fitness enthusiasts',
      targetAudienceOptionFitnessHint:
        'Performance comparison with a premium lens',
      platformsOptionTikTok: 'TikTok',
      platformsOptionTikTokHint: 'A bold opening with natural TikTok pacing',
      platformsOptionReels: 'Instagram Reels',
      platformsOptionReelsHint:
        'A clear product demonstration in everyday situations',
      platformsOptionShorts: 'YouTube Shorts',
      platformsOptionShortsHint:
        'Clear demonstration with search-friendly framing',
      platformsOptionAllShorts: 'TikTok + Reels + Shorts',
      platformsOptionAllShortsHint:
        'One flexible vertical cut for all short-form channels',
      campaignGoalOptionLaunch: 'Product launch',
      campaignGoalOptionLaunchHint:
        'Introduce the product and create first-wave demand',
      campaignGoalOptionConversions: 'Drive conversions',
      campaignGoalOptionConversionsHint:
        'Push viewers toward purchase or sign-up',
      campaignGoalOptionBenefits: 'Explain benefits',
      campaignGoalOptionBenefitsHint:
        'Make the core value obvious and memorable',
      campaignGoalOptionRetarget: 'Retarget shoppers',
      campaignGoalOptionRetargetHint:
        'Bring interested audiences back and prompt them to purchase',
      submit: 'Create video concept options',
    },
    promptBox: {
      dropMedia: 'Drop image or video',
      placeholder:
        'Start with your idea. Type @ for References or / for Agent Skills.',
    },
    conceptPicker: {
      title: 'Choose a concept board',
      description:
        'Preview the concept images in the gallery, then pick a direction here.',
      generatedImages: 'Generated {{count}} images',
      generatingImages: 'Generating {{count}} images…',
      generatedDirections: 'Generated {{count}} creative directions',
      generatedImage: 'Generated image',
      imageLabel: 'Image {{index}}',
      directionLabel: 'Direction {{index}}',
      chooseDirection: 'Choose this direction',
      ready: 'Ready',
      generating: 'Generating…',
      expandItem: 'Expand {{label}}',
      collapseItem: 'Collapse {{label}}',
      previewImage: 'Preview {{label}} in gallery',
      useConceptImage: 'Use concept image {{index}}: {{title}}',
      useConceptDirection: 'Use direction {{index}}: {{title}}',
      useVideoImage: 'Use image {{index}} for video generation',
    },
    finalImagesTool: {
      title: 'Final campaign images',
      description_one: '{{count}} high-resolution variant in the same style',
      description_other: '{{count}} high-resolution variants in the same style',
      generating: 'Generating 3 high-resolution images…',
      concept: 'From concept: {{title}}',
    },
    gallery: {
      hideWatermark: 'Hide watermark',
      watermarkAllResources: 'Applies to all generated images and videos',
      preparingDownload: 'Preparing…',
      downloadFailed: 'Download failed. Please try again.',

      generations: 'Results',
      cuts: 'Cuts',
      back: 'Back to stage',
      generationStages: 'Generation stages',
      filter: 'Filter gallery',
      layout: 'Change layout',
      addToFavorites: 'Add to favorites',
      removeFromFavorites: 'Remove from favorites',
      moreActions: 'More actions',
      selectAsset: 'Select asset',
      addToCut: 'Add to Cut',
      download: 'Download',
      copyLink: 'Copy Link',
      delete: 'Delete',
      loading: 'Loading gallery…',
      loadingHint: 'Fetching session assets from storage.',
      emptyTitle: 'No results yet',
      emptyHint:
        'Upload a product photo or generate campaign visuals. They will appear here as you work through the session.',
      generatedDialogueAudio: 'Generated dialogue audio',
    },
    videoJobTool: {
      title: 'Creating ad video',
      description: 'Generating a new video from your reference media',
      progressLabel: 'Estimated video generation progress',
      percent: 'Estimated progress {{progress}}%',
      canvasHint: 'The result will appear on this stage when it is ready.',
      direction: 'Direction: {{title}}',
      job: 'job {{id}}',
      status: {
        queued: 'Video job is queued…',
        processing: 'Rendering your ad video…',
        succeeded: 'Video ready.',
        failed: 'Video generation failed.',
        cancelled: 'Video generation was cancelled.',
      },
    },
    navigation: {
      home: 'Home',
      agent: 'Agent',
      generate: 'Generate',
      assets: 'Assets',
      primary: 'Primary navigation',
      mobile: 'Mobile studio navigation',
      close: 'Close navigation',
      myloLab: 'Mylo lab',
    },
    shell: {
      currentRegion: 'Current Region: {{region}}',
      switchRegion: 'Switch Region?',
      switchRegionDescription:
        'Switching Region requires a new login. Workspace data will not move between Regions.',
      cancel: 'Cancel',
      switch: 'Switch Region',
    },
    home: {
      greeting: 'Hi {{name}}, what do you want to create?',
      getStarted: 'Get started',
      presetCategories: 'Preset categories',
      creativeShortcuts: 'Creative shortcuts',
      creativePresets: 'Creative presets',
      allSkills: 'All Skills',
      shortcuts: {
        editTimeline: 'Go to editor',
        seedance: 'Мыло Video 2.5',
        doubao: 'Doubao',
        adCampaign: 'Ad Campaign',
        moodBoard: 'Mood Board',
      },
      tabs: {
        marketing: 'Marketing campaigns',
        movies: 'Movies',
        social: 'Social media',
        education: 'Educational content',
        experimental: 'Experimental art',
        other: 'Other',
      },
      presets: {
        adapt: 'Adapt one idea for every platform',
        posts: 'Create a week of posts',
        remix: 'Remix a trend with my product',
        thumbnails: 'Generate a thumbnail pack',
        reel: 'Make a short vertical reel',
      },
    },
    assets: {
      eyebrow: 'Workspace library',
      title: 'Assets',
      description:
        'Reuse images, videos, and audio across your Agent sessions.',
      upload: 'Upload',
      uploading: 'Uploading · {{count}}',
      search: 'Search assets',
      searchPlaceholder: 'Search assets…',
      sessionGenerations: 'Generations from this session',
      sessionGenerationsError:
        'Could not load generations from this session. Try again.',
      sessionGenerationsEmptyTitle: 'No generations in this session yet',
      sessionGenerationsEmptyDescription:
        'Generated images, videos, and concepts will appear here.',
      showAll: 'Show all assets',
      retry: 'Retry',
      tryAgain: 'Try again or choose another asset.',
      errors: {
        deleteForbidden:
          'Only the asset owner or a workspace admin can delete it.',
        deleteInUse:
          'This asset is still used by a generation or project. Remove it there before deleting it from the library.',
        deleteMissing:
          'This asset is no longer available. Refresh the library and try again.',
        deleteFailed: 'The asset could not be deleted. Try again.',
        favoriteFailed: 'The favorite could not be updated. Try again.',
      },
      deleteConfirm: 'Delete {{name}}?',
      deleteDescription: 'This removes {{name}} from the workspace library.',
      cancel: 'Cancel',
      emptyTitle: 'No assets yet',
      emptyDescription: 'Upload an image, video, or audio file.',
      select: 'Select {{name}}',
      preview: 'Preview {{name}}',
      loadingMore: 'Loading more…',
      retryLoadingMore: 'Retry loading more',
      retryLoading: 'Retry loading assets',
      pickerDescription: 'Add workspace media to this message.',
      uploadMedia: 'Upload media',
      dropOrUpload: 'Drop or upload files',
      mediaTypes: 'Image, video, audio',
      image: 'Image',
      video: 'Video',
      audio: 'Audio',
      loadingVideo: 'Loading video…',
      file: 'File',
      delete: 'Delete',
      editVideo: 'Edit video',
      deleteLabel: 'Delete {{name}}',
      editVideoLabel: 'Edit video {{name}}',
      uploadActivity: 'Upload activity',
      uploads: 'Uploads',
      activeUploads: '{{count}} active',
      retryUpload: 'Retry {{name}}',
      dismissUpload: 'Dismiss {{name}}',
      status: {
        queued: 'Queued',
        preparing: 'Preparing',
        uploading: 'Uploading',
        complete: 'Complete',
        failed: 'Failed',
      },
    },
    videoEditor: {
      includeWatermark: 'Keep watermark',
      chooseTitle: 'Choose a video to edit',
      chooseDescription:
        'Select a workspace video to create a timeline and start editing.',
      opening: 'Opening {{name}}…',
      openError: 'The editor could not open this video. Choose another clip.',
      videoAssets: 'Video assets',
      searchAssets: 'Search assets',
      searchAssetsPlaceholder: 'Search assets…',
      uploadFiles: 'Drop or upload files',
      loadingAssets: 'Loading assets',
      timeline: 'Video timeline',
      playhead: 'Playhead',
      clip: 'Clip',
      preparingMedia: 'Preparing media…',
      preview: 'Video preview',
      playPreview: 'Play preview',
      pausePreview: 'Pause preview',
      tools: 'Editing tools',
      undo: 'Undo',
      redo: 'Redo',
      split: 'Split',
      mute: 'Mute',
      unmute: 'Unmute',
      deleteClip: 'Delete clip',
      clipActions: 'Clip actions',
      editClip: 'Edit clip',
      splitShots: 'Split shots',
      splitAudio: 'Split audio',
      splitAudioUnavailable: 'Separate audio tracks are not available yet',
      rippleDelete: 'Ripple delete',
      all: 'All',
      uploads: 'Uploads',
      folders: 'Folders',
      favorites: 'Favorites',
      addFavorite: 'Add {{name}} to favorites',
      removeFavorite: 'Remove {{name}} from favorites',
      recents: 'Recents',
      supportedMedia: 'Image, video, PDF, audio',
      noVideoAssets: 'No video assets',
      uploadClipHint: 'Upload a clip to get started.',
      selectClip: 'Select clip {{id}}',
      trimStart: 'Trim start of clip {{id}}',
      trimEnd: 'Trim end of clip {{id}}',
      muteClip: 'Mute clip',
      unmuteClip: 'Unmute clip',
      loadError:
        'Video project could not be loaded. Return to Assets and try opening the source again.',
      exportUnavailable: 'Export status unavailable',
      exportRevision: 'Export new revision',
      exported: 'Exported',
      exporting: 'Exporting {{progress}}%',
      export: 'Export',
      back: 'Back',
      projectName: 'Project name',
      renameFailed: 'Rename failed',
      saving: 'Saving…',
      saveFailed: 'Save failed',
      retrySave: 'Retry save',
      newerVersion: 'Newer version available',
      saved: 'Saved',
      retryExport: 'Retry failed export',
      downloadMp4: 'Download MP4',
      download: 'Download',
      closePanel: 'Close side panel',
      openPanel: 'Open side panel',
      recoveredDraft: 'Recovered video draft',
      recoveryAvailable: 'Unsaved edits from this browser are available.',
      restoreEdits: 'Restore edits',
      discard: 'Do not restore',
      addAssetFailed:
        'This media could not be added. Check the file and try again.',
      chooseAssetAgain: 'Choose media',
      createCut: 'Create cut',
      generations: 'Results',
      sessionGenerationsEmpty: 'Generations will show here',
      cuts: 'Cuts',
      newCut: 'New version',
      creatingCut: 'Creating…',
      createCutFailed: 'Could not create cut. Try again.',
      emptyCut: 'Add media to start this cut',
      selectOrDropMedia: 'Select or drop video, audio, or images',
      cutActions: 'Actions for {{name}}',
      renameCut: 'Rename',
      duplicateCut: 'Duplicate',
      deleteCut: 'Delete',
      deleteCutConfirmTitle: 'Delete this version?',
      deleteCutConfirmDescription:
        '“{{name}}” will be permanently deleted. This action cannot be undone.',
      cancel: 'Cancel',
      assetDialogTitle: 'Add media',
      closeAssetDialog: 'Close asset picker',
      noFavoriteAssets: 'No favorite assets',
      noMediaAssets: 'No assets yet',
      keyboardShortcuts: 'Keyboard shortcuts',
      shortcuts: {
        playbackNavigation: 'Playback & navigation',
        playPause: 'Play / pause',
        skipBack: 'Skip back 5s',
        skipForward: 'Skip forward 5s',
        stepBack: 'Step back 1 frame',
        stepForward: 'Step forward 1 frame',
        previousEdge: 'Previous clip edge',
        nextEdge: 'Next clip edge',
        jumpStart: 'Jump to start',
        jumpEnd: 'Jump to end',
        zoomIn: 'Zoom in',
        zoomOut: 'Zoom out',
        selecting: 'Selecting',
        selectAll: 'Select all clips',
        selectAcross: 'Select across all tracks',
        selectTrack: 'Select on one track',
        markStart: 'Mark selection start',
        markEnd: 'Mark selection end',
        clearSelection: 'Clear selection',
        editing: 'Editing',
        split: 'Split at playhead',
        copy: 'Copy selected clips',
        paste: 'Paste at playhead',
        deleteGap: 'Delete without shifting clips',
        rippleDelete: 'Delete and shift clips left',
        undo: 'Undo',
        redo: 'Redo',
        duplicate: 'Duplicate while dragging',
      },
      shortcutPlay: 'Play / pause',
      shortcutSkip: 'Skip 5 seconds',
      shortcutFrame: 'Step one frame',
      shortcutEdge: 'Previous / next clip edge',
      shortcutSplit: 'Split at playhead',
      shortcutDelete: 'Delete and leave gap',
      shortcutRippleDelete: 'Delete and shift left',
      shortcutHistory: 'Undo / redo',
      zoomOut: 'Zoom out',
      zoomIn: 'Zoom in',
      fitTimeline: 'Fit timeline to view',
      addClip: 'Add clip',
      preparingMetadata: 'Preparing video metadata…',
      mobileEditingHint:
        'Dragging and edge trimming are available on desktop. Preview, export, and toolbar commands remain available here.',
      assets: 'Assets',
      agent: 'Agent',
      editWithAgent: 'Edit with Agent',
      agentDescription:
        'Describe the sequence you want to build from your assets.',
      sendMessage: 'Send a message',
      sendMessagePlaceholder: 'Send a message…',
    },
    agentUi: {
      defaultChat: 'Agent chat',
      justNow: 'Just now',
      recents: 'Recents',
      recentChats: 'Recent chats',
      recentChatsLabel: 'Recent agent chats',
      allChats: 'All chats',
      viewAllChats: 'View all chats',
      closeChatHistory: 'Close chat history',
      loadingChats: 'Loading chats…',
      loadingMoreChats: 'Loading more chats…',
      retryLoadingMoreChats: 'Retry loading more chats',
      sessionGenerating: 'Generating',
      sessionQueued: 'Queued — not generating yet',
      sessionUnread: 'Unread',
      sessionAwaitingUser: 'Waiting for your answer',
      noChats: 'No chats yet. Start a new chat to see it here.',
      sessionActions: 'Session actions',
      sessionActionsFor: 'Actions for {{title}}',
      chatActions: 'Chat actions',
      newChat: 'New chat',
      newChatTooltip: 'New Chat',
      rename: 'Rename',
      delete: 'Delete',
      seeGenerationsInAssets: 'See generations in Assets',
      deleteSession: 'Delete session',
      deleteSessionConfirm: 'Delete “{{title}}”?',
      deleteSessionDescription:
        'This removes the session from your recent chats. This action cannot be undone.',
      deleteSessionFailed: 'Could not delete this session. Try again.',
      renameChat: 'Rename chat',
      chatTitle: 'Chat title',
      titleRequired: 'Title is required',
      renameFailed: 'Could not rename this chat',
      cancel: 'Cancel',
      save: 'Save',
      skills: 'Skills',
      creationMode: 'Creation mode',
      agentMode: 'Agent mode',
      imageGeneration: 'Image generation',
      videoGeneration: 'Video generation',
      model: 'Model',
      aspectRatio: 'Aspect ratio',
      smartAspectRatio: 'Smart ratio',
      resolution: 'Resolution',
      quantity: 'Quantity',
      duration: 'Duration',
      seconds: 's',
      generateAudio: 'Audio',
      quoteFailed: 'Unable to estimate Soaps',
      quoteRequiresPlan:
        'The selected model requires an active paid plan. Subscribe or renew your plan to continue.',
      viewPlans: 'View plans',
      retryQuote: 'Retry estimate',
      quoteUnavailable: 'Unavailable',
      estimatedCredits: 'Estimated cost',
      calculatingCredits: 'Calculating…',
      videoReferenceMode: 'Reference mode',
      allReference: 'All references',
      firstFrame: 'First frame',
      videoContinuation: 'Continue video',
      imagePromptPlaceholder: 'Describe the image you want to create…',
      videoPromptPlaceholder:
        'Describe the motion and scene you want to create…',
      directCreationProgress: {
        preparing: {
          title: 'Preparing generation',
          detail: 'Preparing your creation settings',
        },
        queued: {
          title: 'Queued',
          detail: 'Your task will start shortly',
        },
        processing: {
          title: 'Generating',
          detail: 'You can keep browsing while this is generated',
        },
      },
      toolCalls: {
        recognizeImage: 'Reading image…',
        recognizeVideo: 'Reading video…',
        updateImage: 'Generating updated image…',
        askUser: 'Opening the details form…',
        calling: 'Calling {{name}}…',
        updatedImage: 'Generated the updated image.',
      },
      attachmentErrors: {
        selectWorkspaceImages: 'Select a workspace before adding images.',
        selectWorkspaceMedia: 'Select a workspace before adding media.',
        imageLoad: 'This image could not be loaded. Try selecting it again.',
        videoLoad: 'This video could not be loaded. Try selecting it again.',
        limit: 'You can attach up to {{count}} assets.',
      },
      loadingChat: 'Loading agent chat…',
      skill: {
        timeline: {
          label: 'Timeline',
          description: 'Plan milestones from concept through final delivery.',
        },
        adCampaign: {
          label: 'Ad Campaign',
          description:
            'Build a campaign from concept to finished video and image assets for every platform.',
        },
        adVideo: {
          label: 'Ad Video',
          description:
            'Create a short-form ad video with a hook and call to action.',
        },
        moodBoard: {
          label: 'Mood Board',
          description:
            'Shape your project with a focused set of visual directions.',
        },
        motionGraphic: {
          label: 'Motion Graphic',
          description: 'Turn text, charts, and graphics into a motion concept.',
        },
      },
      assets: 'Assets',
      gallery: 'Session gallery',
      galleryTitle: 'Gallery',
      galleryDescription:
        'Images collected from uploads, references, concepts, and generated results in this session.',
      galleryLoading: 'Loading gallery…',
      galleryEmpty: 'No images yet',
      galleryLoadingDescription: 'Fetching session assets from storage.',
      galleryEmptyDescription:
        'Upload a product photo or generate campaign visuals. They will appear here as an album while the conversation stays on the right.',
      assetKind: {
        upload: 'upload',
        reference: 'reference',
        concept: 'concept',
        generated: 'generated',
      },
      generatedVideo: 'Generated ad video',
      generatedImage: 'Generated image',
      galleryLabels: {
        audio: 'audio',
        upload: 'Upload',
        uploads: 'Uploads',
        video: 'video',
        reference: 'Reference',
        references: 'References',
        productReference: 'Product reference',
        concepts: 'Concepts',
        generated: 'Generated',
        generatedImage: 'Generated image',
        generatedVideo: 'Generated video',
      },
      previewImage: 'Preview image',
      previewAsset: 'Preview {{name}}',
      contextAssetUnavailable: 'Preview unavailable',
      playMedia: 'Play {{name}}',
      copyMessage: 'Copy message',
      messageCopied: 'Copied',
      stopGenerating: 'Stop generating',
      sendMessage: 'Send message',
      removeAttachment: 'Remove {{name}}',
      clear: 'Clear',
      clearComposer: 'Clear prompt and references',
      attachment: 'attachment',
      addMoreMedia: 'Add more images or videos',
      uploadMedia: 'Upload image or video',
      references: 'References',
      renameReference: 'Rename {{name}}',
      creativeShortcuts: 'Creative shortcuts',
      creativePresets: 'Creative presets',
      agentPrompt: 'Agent prompt',
      openAssets: 'Open assets',
      openSkills: 'Open skills',
      composerTitle: 'What should we build with Agent?',
      composerDescription: 'Create, analyze and scale your content with Agent.',
      generationSettings: 'Generation settings',
      generationSettingsDescription: 'Choose when and how Agent generates.',
      whenGenerating: 'When generating media',
      optimizeGenerations: 'Optimize generations',
      generationAsk: 'Require confirmation',
      generationAuto: 'Auto',
      generationQuality: 'Quality',
      generationSpeed: 'Speed',
      generationCost: 'Cost',
      generationCustom: 'Custom',
      freePlanGenerationLimit:
        'Free plan: image preferences still apply; video model options are limited to 720p, with one video job at a time.',
      autoCreditLimit: 'Auto limit per operation',
      customGenerationInstructions: 'Custom preferences',
      customGenerationPlaceholder:
        'Preferred models, resolution, aspect ratio, or budget…',
      setGenerationDefault: 'Set as default for new sessions',
      savingGenerationDefault: 'Saving default…',
      planTitle: 'Agent plan',
      planThinking: 'Planning the next steps…',
      planStepCount_one: '{{count}} planned step',
      planStepCount_other: '{{count}} planned steps',
      planStatus: 'Step status: {{status}}',
      planStepProgress: '{{step}} progress',
      planEdit: 'Edit plan',
      planEditSummary: 'Action summary',
      planEditModel: 'Model',
      planModelAutomatic: 'Automatic',
      planEditPrompt: 'Prompt',
      planEditParameters: 'Parameters (JSON)',
      planEditOutputCount: 'Outputs',
      planEditReferences: 'Targets and references',
      planEditReferenceRole: 'Reference role',
      planEditRemoveReference: 'Remove reference',
      planEditAddReference: 'Add a reference',
      planEditSelectReference: 'Select an asset…',
      planEditAdd: 'Add',
      planEditCancel: 'Cancel',
      planEditSave: 'Save and re-quote',
      planEditSaving: 'Re-quoting…',
      planEditInvalidParameters: 'Enter parameters as a JSON object.',
      planEditFailed:
        'The plan could not be updated. Check the fields and try again.',
      planAssumptions: 'What the Agent considered',
      planDetails: 'Details',
      planFailureRealPerson:
        'This provider cannot animate this image because it may depict a real person. Try an illustrated or clearly synthetic character image.',
      planFailureGeneric:
        'Generation failed. Review the inputs or settings, then retry.',
      approvePlanStepPrefix: 'Confirm ·',
      approvingPlanStep: 'Confirming…',
      applyTimelineEdit: 'Apply changes',
      timelineEditPreview: '{{count}} timeline changes',
      timelineEditTarget: '{{name}} · revision {{revision}}',
      planPricingUnavailable: 'Pricing unavailable — execution is disabled',
      retryPlanStep: 'Retry',
      retryFailedOutputs: 'Retry failed ({{count}})',
      skipPlanStep: 'Skip',
      saveWorkspaceReference: 'Save as Workspace Reference',
      planInputs: 'Source materials',
      planInputRole: {
        target: 'Target',
        reference: 'Reference',
        identity_reference: 'Identity reference',
        motion_reference: 'Camera & motion reference',
        first_frame: 'First frame',
        source: 'Source',
      },
      planConstraint: { keep: 'Keep', vary: 'Vary' },
      planExecutionStatus: {
        awaiting_confirmation: 'Waiting for your confirmation…',
        queued: 'Queued — not generating yet',
        processing: 'Generating media…',
        succeeded: 'Generation complete',
        failed: 'Generation failed. You can keep chatting with Agent.',
        cancelled: 'Generation cancelled. You can keep chatting with Agent.',
      },
      planResultComplete: 'Completed: {{summary}}',
      planResultCompleteWithOutputs:
        'Completed: {{summary}} Generated {{count}} result(s), which you can continue refining in chat.',
      planResultFailed:
        '{{count}} plan step(s) failed. Open the plan for details and retry.',
    },
    videoParams: {
      eyebrow: 'New video output',
      description:
        'Generate a fresh ad video from the uploaded image and video context.',
      duration: 'Duration (sec.)',
      durationHint: 'Whole seconds from 4 to 30.',
      durationError: 'Duration must be between 4 and 30 seconds.',
      aspectRatio: 'Aspect ratio',
      resolution: 'Resolution',
      modelSelection: 'Model selection',
      auto: 'Auto recommend',
      manual: 'Choose a model',
      model: 'Video model',
      paid: 'Paid plan',
      badges: {
        economy: 'Economy',
        fast: 'Fast',
        professional: 'Professional',
        latest: 'Latest',
      },
      dynamicDurationHint: 'Whole seconds from 4 to {{max}}.',
      audio: 'Generate native audio',
      audioHint: 'Dialogue, ambience, and sound effects when supported.',
      adjusted: 'Output adjusted: {{before}} → {{after}}',
      calculating: 'Calculating Soaps…',
      estimatedPrefix: 'Estimated cost:',
      estimated: 'Estimated cost in credits: {{count}}',
      adjust: 'Adjust settings to refresh the quote.',
      quoteErrorHint: 'Change a video setting to calculate the quote again.',
      insufficientCredits:
        'Video generation could not start because there are not enough available credits. This request needs {{count}} credits. Your settings have been kept; add credits or change the video settings and try again.',
      generationFailed:
        'Video generation could not start: {{message}} Your settings have been kept so you can try again.',
      requestMessage:
        'Generate video: “{{direction}}” · {{duration}}s · {{ratio}} · {{resolution}}',
      generate: 'Generate video',
      generatePrefix: 'Generate ·',
      generateWithCredits: 'Generate · {{count}} credits',
    },
    assetConfirmation: {
      accept: 'Accept',
      acceptHint: 'Use this observation as context for generation',
      ignore: 'Ignore',
      ignoreHint: 'Do not use this low-confidence observation',
      closeTool: 'Close material confirmation',
      previousQuestion: 'Previous question',
      nextQuestion: 'Next question',
      stepOf: 'of',
      skip: 'Skip',
      required: 'Required',
      requiredHint: 'Answer every question to continue generation.',
      submit: 'Confirm & Continue',
    },
    directVideoSetup: {
      ratioEyebrow: 'Video frame',
      invalidRatio: '{{ratio}} is not supported. Choose an available ratio.',
      chooseRatio: 'Choose a video aspect ratio',
      ratioSelected: 'Selected {{ratio}}.',
      ratioSelectedChooseDirection:
        'Selected {{ratio}}. Now choose a video direction.',
      direction: 'Video direction',
      directionQuestion: 'How should the image move?',
      directionPlaceholder: 'Describe motion, camera, and pacing',
      subtle: 'Subtle motion',
      subtleHint: 'Preserve the composition with natural micro-motion',
      subtleValue:
        'Preserve the composition and subject, adding only subtle natural motion',
      pushIn: 'Cinematic push-in',
      pushInHint: 'Move slowly toward the subject',
      pushInValue:
        'Slowly push the camera toward the subject with cinematic depth',
      orbit: 'Orbit showcase',
      orbitHint: 'Move smoothly around the subject to reveal detail',
      orbitValue:
        'Move the camera smoothly around the subject while preserving its details',
      dynamic: 'Dynamic reveal',
      dynamicHint: 'Use stronger motion and a clear visual beat',
      dynamicValue:
        'Reveal the subject progressively with dynamic camera motion and clear pacing',
      typeOwnAnswer: 'Enter a custom direction',
      customLabel: 'Custom {{field}}',
      closeTool: 'Close direction selector',
      previousQuestion: 'Previous question',
      nextQuestion: 'Next question',
      stepOf: 'of',
      skip: 'Skip',
      required: 'required',
      continue: 'Continue',
      submit: 'Use Direction',
    },
  },
} as const satisfies TranslationResources;
